import React from 'react';

import Loadable from 'react-loadable';
// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
import { v4 as uuid } from 'uuid';

import { DefaultExtensionProvider, type Parameters } from '@atlaskit/editor-common/extensions';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import { token } from '@atlaskit/tokens';
import { DEFAULT_GRAY_COLOR } from '@atlassian/color-picker';
import {
	CornerStyle,
	type CustomSitesExtensionProviderProps,
	getExtensionManifest,
	type HubDefaultParameters,
	RenderVariant,
	Height as Size,
} from '@atlassian/company-hub-common';

import {
	CARDS_EXTENSION_KEY,
	CARDS_EXTENSION_TYPE,
	CARDS_EXTENSION_VERSION_5,
	CARDS_IMAGES_V5,
} from '../../constants';
import {
	Alignment,
	type CardsExtensionParameters,
	CardsLayout,
	ImagePosition,
	Orientation,
} from '../../types';
import { type CardsExtensionProps } from '../cards-extension';

import { messages } from './messages';

const CardsExtension = Loadable({
	loader: async () =>
		(await import(/* webpackChunkName: "@atlaskit-internal_CardsExtension" */ '../cards-extension'))
			.CardsExtension,
	loading: () => <UFOLoadHold name="cards-extension-loader"></UFOLoadHold>,
});

export const createCard = (
	imageSrc: string,
	imageAltText: string,
): {
	// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
	cardId: any;
	description: string;
	imageAltText: string;
	imagePosition: ImagePosition;
	imageSrc: string;
	isDescriptionUserEdited: boolean;
	isLinkUserEdited: boolean;
	isTitleUserEdited: boolean;
	link: string;
	title: string;
} => ({
	// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
	cardId: uuid(),
	link: '',
	isLinkUserEdited: false,
	title: '',
	isTitleUserEdited: false,
	description: '',
	isDescriptionUserEdited: false,
	imageSrc,
	imagePosition: ImagePosition.MIDDLE,
	imageAltText,
});

let cards: ReturnType<typeof createCard>[] | null = null;
export const getDefaultCards = (): {
	// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
	cardId: any;
	description: string;
	imageAltText: string;
	imagePosition: ImagePosition;
	imageSrc: string;
	isDescriptionUserEdited: boolean;
	isLinkUserEdited: boolean;
	isTitleUserEdited: boolean;
	link: string;
	title: string;
}[] => {
	if (cards) {
		return cards;
	}

	return (cards = CARDS_IMAGES_V5.map(({ src, alt }) => createCard(src, alt)));
};

type GetCardsExtensionParametersProps = {
	defaultParameters?: HubDefaultParameters;
	extensionTitle: string;
};

export const getCardsExtensionParameters = ({
	extensionTitle,
	defaultParameters,
}: GetCardsExtensionParametersProps): CardsExtensionParameters => {
	const parameters: CardsExtensionParameters = {
		size: Size.MEDIUM,
		cards: getDefaultCards(),
		isAvatarShown: false,
		isPublishDateShown: false,
		extensionTitle,
		version: CARDS_EXTENSION_VERSION_5,
		renderVariant: RenderVariant.MANUAL,
		isSegmentationShown: false,
		orientation: Orientation.STACKED,
		alignment: Alignment.CENTER,
		isAutoscrollEnabled: false,
		backgroundColor: defaultParameters?.backgroundColor ?? token('elevation.surface.overlay'),
		textColor: defaultParameters?.textColor ?? token('color.text'),
		cornerStyle: CornerStyle.ROUNDED,
		isSocialStatsShown: false,
		isIconShown: true,
		layout: CardsLayout.FIXED_IMAGE_HEIGHT,
		isPaddingEnabled: true,
		isLabelShown: true,
		borderColor: DEFAULT_GRAY_COLOR,
		isDescriptionShown: true,
	};

	return parameters;
};

export const cardsExtensionProvider = ({
	contentId,
	propsProvider,
	mode,
	defaultParameters,
}: CardsExtensionProps) => {
	return async ({
		editorActions,
		openConfigPanel,
		intl,
		setUpsellModalSource,
	}: CustomSitesExtensionProviderProps): Promise<DefaultExtensionProvider<Parameters>> => {
		const parameters = getCardsExtensionParameters({
			extensionTitle: intl.formatMessage(messages.linkCardsMacroTitle),
			defaultParameters,
		});
		return new DefaultExtensionProvider([
			getExtensionManifest<CardsExtensionParameters>({
				title: intl.formatMessage(messages.linkCardsMacroTitle),
				type: CARDS_EXTENSION_TYPE,
				key: CARDS_EXTENSION_KEY,
				description: intl.formatMessage(messages.linkCardsMacroDescription),
				icons: {
					'48': () =>
						import(
							/* webpackChunkName: "@atlaskit-internal_loadable-CardsExtensionIcon" */
							'../assets/cards-extension-icon'
						).then((mod) => mod.CardsExtensionIcon),
				},
				parameters,
				update: async () => {
					const selectedNode = editorActions?.getSelectedNode()?.toJSON();
					const selectedLocalId = selectedNode?.attrs?.localId;

					if (!!selectedLocalId && openConfigPanel) {
						openConfigPanel(selectedLocalId);
					}
				},
				render:
					async () =>
					({ node }: any) => {
						return (
							<CardsExtension
								extensionNode={node}
								contentId={contentId}
								propsProvider={propsProvider}
								mode={mode}
							/>
						);
					},
				quickInsertAction: async () =>
					Promise.resolve({
						type: 'extension',
						key: CARDS_EXTENSION_KEY,
						attrs: {
							extensionType: CARDS_EXTENSION_TYPE,
							extensionKey: CARDS_EXTENSION_KEY,
							layout: 'default',
							parameters,
						},
					}),
				source: 'premiumCardsInsert',
				setUpsellModalSource,
			}),
		]);
	};
};
